import { useEffect, useRef, useState } from "react";
import { MOTION_PICK_MIN } from "@fiket/booth-schema";
import { useRouter } from "../../store/router";
import { useBoothCaptionEnabled, useBoothConfig } from "../../store/booth";
import { useI18n } from "../../i18n/useI18n";
import { useBranding } from "../../hooks/useBranding";
import { useSelectedStyle, useOutputAspect, styleName } from "../../hooks/useStyles";
import { generateBackRoute } from "./videoFlow";
import { videoInputFor } from "./videoInput";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { PhotoPrint } from "../../components/ui/PhotoPrint";
import { trackError } from "../../telemetry/activity";

/**
 * STAGE · VideoGenerate — 변환 사진으로 영상 생성 job 을 **시작만** 하고 받기 화면으로 넘어간다 (BOOTH-60 지연 전달).
 * 손님은 Veo 완료(1~2분)를 키오스크에서 기다리지 않는다 — 서버가 스케줄러로 완료를 세션에 붙이고, 받기 화면과 QR 공유 페이지가
 * `GET /api/sessions/:code/video` 를 폴링해 보여준다. 이 화면은 job 접수(수 초) 동안만 보이며 실패 처리의 단일 지점이다.
 * 본문에 부스 id(서버 영상 스위치 게이트) 와 고른 동작 id(1개 이상 골랐을 때만, 순서대로) 를 함께 보낸다 (BOOTH-60 1b).
 * 자막을 남겼으면 `caption`(정규화 문자열) + `captionImage`(캔버스 PNG dataURL) 를 **둘 다** 싣는다 — 서버 ffmpeg 가 띠 위에 얹는다 (BOOTH-85).
 * 결제 유효 ON 이면 본문에 paymentId 를 싣고(서버 게이트: paid + 영상 항목), 결제 뒤엔 뒤로가기·재시도를 닫는다 (BOOTH-61/74).
 * 프롬프트 합성은 서버 몫 — 키오스크는 동작 id 만 안다.
 */

interface StartResponse {
  success: boolean;
  data?: { jobId: string };
  /** `details.jobId` — 409 VIDEO_ALREADY_STARTED 때 서버가 알려 주는 이미 진행 중인 job (같은 결제의 중복 요청 경합) */
  error?: { code: string; message: string; details?: { jobId?: string } };
}

export function VideoGenerateView() {
  const go = useRouter((s) => s.go);
  const styleId = useRouter((s) => s.selectedStyleId);
  const boothId = useBoothConfig((s) => s.config?.booth.id ?? null);
  const selectedMotionIds = useRouter((s) => s.selectedMotionIds);
  const hasMotions = selectedMotionIds.length >= MOTION_PICK_MIN;
  const captionEnabled = useBoothCaptionEnabled();
  const caption = useRouter((s) => s.videoCaption);
  const captionImage = useRouter((s) => s.videoCaptionImage);
  const hasCaption = Boolean(caption) && Boolean(captionImage);
  // 영상 입력: 워터마크 없는 사본 → 변환 결과 → 원본 순 (BOOTH-103).
  const shot0 = useRouter((s) => s.capturedShots[0] ?? null);
  const transformedDataUrl = shot0 ? videoInputFor(shot0) : null;
  const setVideoJob = useRouter((s) => s.setVideoJob);
  const setVideoFailed = useRouter((s) => s.setVideoFailed);
  const clearVideo = useRouter((s) => s.clearVideo);
  const paymentId = useRouter((s) => s.paymentId);
  const paid = paymentId !== null;
  const visitId = useRouter((s) => s.visitId);
  // 영상 항목이 실제로 결제됐을 때만 환불 안내·재시도 숨김 (BOOTH-80) — 사진만 결제한 부스(영상 결제 OFF)는 영상이 무료라 재시도 가능
  const videoCharged = useRouter((s) => s.paymentVideoCharged);
  const { t, language } = useI18n();
  const { wordmark } = useBranding();
  const style = useSelectedStyle();
  const aspect = useOutputAspect();

  const [error, setError] = useState<string | null>(null);
  /**
   * 서버가 실패를 인지한 경우에만 자동 환불(F36/F37)이 돌았다 — 요청이 서버에 닿지 못한 전송 실패나
   * VIDEO_ALREADY_STARTED(Ruling 15, 환불 없음)엔 환불을 약속하지 않는다.
   */
  const [serverFailure, setServerFailure] = useState<{ code: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const attemptRef = useRef(0);

  useEffect(() => {
    const current = ++attemptRef.current;
    setError(null);
    setServerFailure(null);
    if (!styleId || !transformedDataUrl) {
      setError(t.videoGenerate.error);
      return;
    }

    let cancelled = false;

    (async () => {
      // 한 틱 양보 — dev StrictMode 의 실행 → 정리 → 재실행에서 정리된 첫 실행이 start 를 보내지 않게(서버 선점 409 이어받기는 안전망).
      await new Promise((r) => setTimeout(r, 0));
      if (cancelled || current !== attemptRef.current) return;
      // start 요청 — JSON 파싱 실패는 전송 오류(serverFailure 없음)
      let startJson: StartResponse;
      let startStatus = 0;
      try {
        const startResp = await fetch("/api/video/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image: transformedDataUrl,
            styleId,
            ...(boothId ? { boothId } : {}),
            ...(hasMotions ? { motionIds: selectedMotionIds } : {}),
            ...(hasCaption ? { caption, captionImage } : {}),
            ...(paymentId ? { paymentId } : {}),
            ...(visitId ? { visitId } : {}),
          }),
        });
        startStatus = startResp.status;
        startJson = (await startResp.json()) as StartResponse;
      } catch (err) {
        // fetch 거부 또는 non-JSON 본문 — 서버가 실패를 인지하지 못한 전송 오류
        if (cancelled || current !== attemptRef.current) return;
        trackError("video_start_failed", err);
        setVideoFailed();
        setError(err instanceof Error ? err.message : t.videoGenerate.error);
        return;
      }
      if (cancelled || current !== attemptRef.current) return;
      // 409 VIDEO_ALREADY_STARTED + details.jobId: 같은 결제로 이미 시작된 job 이 있다(dev StrictMode 이중 effect·이중 탭).
      // 서버는 Veo 를 다시 부르지 않았으니 그 job 을 그대로 이어받는다 — 손님에겐 오류가 아니다.
      const attachedJobId =
        !startJson.success && startJson.error?.code === "VIDEO_ALREADY_STARTED" ? startJson.error.details?.jobId : undefined;
      if (!attachedJobId && (!startJson.success || !startJson.data)) {
        // 서버가 명시적으로 실패 응답 — 자동 환불(F36)이 돌았을 수 있다
        const serverCode = startJson.error?.code ?? "VIDEO_START_FAILED";
        // 서버 메시지·상태를 상세로 (BOOTH-109) — 행 message 는 그대로 code 한 줄
        trackError("video_start_failed", undefined, {
          message: startJson.error?.message ? `${serverCode}: ${startJson.error.message}` : serverCode,
          serverCode,
          ...(startStatus ? { httpStatus: startStatus } : {}),
        });
        setServerFailure({ code: serverCode });
        setVideoFailed();
        setError(startJson.error?.message ?? t.videoGenerate.error);
        return;
      }
      const jobId = attachedJobId ?? startJson.data!.jobId;
      // job 접수 — 완료는 서버 몫. 받기 화면이 세션을 만들 때 videoJobId 를 실어 서버가 붙이고, 이후 상태를 폴링한다.
      setVideoJob(jobId);
      go("printShare");
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  return (
    <div className="view view--stackable">
      {paid ? null : (
        <BackButton
          label={t.videoGenerate.back}
          onClick={() => {
            clearVideo();
            // 바로 앞 화면 = 자막 입력 (BOOTH-85) — 부스 자막 단계가 꺼져 있으면 동작 선택 또는 소개
            go(generateBackRoute(captionEnabled, hasMotions));
          }}
        />
      )}
      <section className="center" aria-live="polite">
        <PhotoPrint
          src={transformedDataUrl}
          aspect={aspect}
          brand={wordmark}
          label={styleName(style, language)}
          size="sm"
          overlay={!error ? <div className="shimmer" aria-hidden /> : null}
        />
        <h1 style={{ marginTop: 18 }}>{t.videoGenerate.title}</h1>
        <p>{t.videoGenerate.caption}</p>
        {!error ? <div className="progress-track" aria-hidden><span /></div> : null}
        {error ? <small style={{ color: "var(--color-error)" }}>{error}</small> : null}
        {error && videoCharged && serverFailure && serverFailure.code !== "VIDEO_ALREADY_STARTED" ? <p className="note" style={{ maxWidth: 520 }}>{t.videoGenerate.refunded}</p> : null}
        {error ? (
          <div className="actions flex gap-[12px]" style={{ marginTop: 14 }}>
            <Button
              variant="secondary"
              onClick={() => {
                clearVideo();
                go("printShare");
              }}
            >
              {t.videoGenerate.skipToPhoto}
            </Button>
            {/* 영상분을 결제한 뒤 실패는 서버가 자동 환불했으므로(F36/F37) 재시도는 400 VIDEO_NOT_PAID 로 끝난다(F41) — 버튼을 두지 않는다 */}
            {videoCharged ? null : (
              <Button onClick={() => { clearVideo(); setAttempt((a) => a + 1); }}>{t.videoGenerate.retry}</Button>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}
